import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { createCart, fetchCart, addCartLine, updateCartLine, removeCartLine } from '../services/shopService';

const CartContext = createContext();

// Per-account localStorage key, same convention as ProfileContext's
// activeProfile persistence -- a stored cart id should never leak across
// different Manager accounts on the same device.
const storageKey = (ownerUserId) => `pumpous.cartId.${ownerUserId}`;

export const CartProvider = ({ ownerUserId, children }) => {
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadCart = useCallback(async () => {
    if (!ownerUserId) {
      setCart(null);
      return;
    }

    setLoading(true);
    let storedId = null;
    try {
      storedId = localStorage.getItem(storageKey(ownerUserId));
    } catch (e) {
      // localStorage may be unavailable (private mode / SSR); degrade gracefully.
      storedId = null;
    }

    if (storedId) {
      const result = await fetchCart(storedId);
      if (result.success) {
        setCart(result.cart);
        setLoading(false);
        return;
      }
      // Stored id is stale (expired cart, or a bad value) -- fall through
      // and start empty; the next add-to-cart creates a fresh one.
    }
    setCart(null);
    setLoading(false);
  }, [ownerUserId]);

  useEffect(() => {
    loadCart();
  }, [loadCart]);

  const persistCartId = (id) => {
    if (!ownerUserId) return;
    try {
      localStorage.setItem(storageKey(ownerUserId), id);
    } catch (e) {
      // Ignore persistence failures; cart still holds in memory this session.
    }
  };

  // No cart yet -> first add-to-cart creates one (cartCreate already accepts
  // an initial line, so this doesn't need a separate empty-cart-then-add
  // round trip). Once a cart exists, every subsequent add is a cartLinesAdd
  // against it.
  const addToCart = async (variantId, quantity = 1) => {
    if (!cart) {
      const result = await createCart(variantId, quantity);
      if (result.success) {
        setCart(result.cart);
        persistCartId(result.cart.id);
      }
      return result;
    }
    const result = await addCartLine(cart.id, variantId, quantity);
    if (result.success) setCart(result.cart);
    return result;
  };

  const updateLineQuantity = async (lineId, quantity) => {
    if (!cart) return { success: false };
    const result = await updateCartLine(cart.id, lineId, quantity);
    if (result.success) setCart(result.cart);
    return result;
  };

  const removeLine = async (lineId) => {
    if (!cart) return { success: false };
    const result = await removeCartLine(cart.id, lineId);
    if (result.success) setCart(result.cart);
    return result;
  };

  return (
    <CartContext.Provider value={{ cart, loading, addToCart, updateLineQuantity, removeLine }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
